import { Injectable, Inject } from "@angular/core";
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from "@angular/common/http";
import { Router } from "@angular/router";
import { JwtHelperService } from "@auth0/angular-jwt";
import { Observable, throwError } from "rxjs";
import { catchError, flatMap } from "rxjs/operators";

import { handleError } from "../shared/handle-error";
import { PerspectiveService } from "../perspective/perspective.service";
import { role, Role } from "../core/role.const";
import { ThemeService } from "../core/theme.service";

import { ChangePassword } from "./change-password-dialog/change-password.model";
import { Profile } from "./profile.model";
import { OAuth2TokenResponseSuccess } from "./oauth2-token-response-success.model";
import { OAuth2TokenResponseError } from "./oauth2-token-response-error.model";
import { UserCredentials } from "../login/user-credentials.model";

@Injectable()
export class AuthService {
    redirectUrl: string;

    private _userProfile: Profile;

    constructor(
        private http: HttpClient,
        private jwtService: JwtHelperService,
        private perspectiveService: PerspectiveService,
        @Inject(role) private role: Role,
        private router: Router,
        private themeService: ThemeService) {

        this._userProfile = JSON.parse(localStorage.getItem("profile"));
    };

    get userProfile(): Profile {
        return this._userProfile;
    }

    get userHasInternalStaffPermission(): boolean {
        return this._userProfile
            ? this._userProfile.role === this.role.internalStaff
            : false;
    }

    get userHasWebAdminPermission(): boolean {
        return this._userProfile
            ? (this._userProfile.role === this.role.internalStaff || this._userProfile.role === this.role.webAdmin)
            : false;
    }

    private handleTokenRequestError(errResponse: HttpErrorResponse) {
        let message: string;

        if (errResponse.error != null && errResponse.error.error === "invalid_grant") {
            const oauthError = errResponse.error as OAuth2TokenResponseError;
            message = oauthError.error_description;
        } else {
            message = `${errResponse.statusText} (${errResponse.status})`;
        }

        return throwError(message);
    }

    changePassword(dto: ChangePassword): Promise<any> {
        const url = "api/profile/change-password";
        return this.http.post(url, dto)
            .toPromise()
            .catch(handleError);
    }

    loggedIn(): boolean {
        if (this.jwtService.tokenGetter()) {
            return (!this.jwtService.isTokenExpired() && this.userProfile != null);
        } else {
            return false;
        }
    }

    login(creds: UserCredentials): Observable<any> {
        const body = `grant_type=password&username=${creds.username}&password=${creds.password}`;

        return this.http
            .post<OAuth2TokenResponseSuccess>("api/connect/token", body, {
                headers: new HttpHeaders().set("Content-Type", "application/x-www-form-urlencoded")
            })
            .pipe(
                catchError(this.handleTokenRequestError),
                flatMap(data => {
                    localStorage.setItem("access_token", data.access_token);

                    return this.http.get<Profile>("api/profile")
                        .toPromise()
                        .then(profile => {
                            localStorage.setItem("profile", JSON.stringify(profile))
                            this._userProfile = profile;

                            // internal staff have their theme set via the perspective component
                            if (!this.userHasInternalStaffPermission) {
                                // themeService has its own "handleError" function
                                this.themeService.getTheme()
                                    .catch(err => console.log("Error retrieving theme settings."));
                            }

                            return profile;
                        })
                        .catch(handleError);
                })
            );
    }

    logout(): void {
        localStorage.removeItem("access_token");
        localStorage.removeItem("profile");
        this._userProfile = null;

        this.themeService.removeActiveTheme();
        // only relevant for internal staff users
        this.perspectiveService.removeActiveIntroducer();
    }
}


// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./src/app/auth/auth.service.ts